import MenuIcon from "@mui/icons-material/Menu";
import { Grid } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import { makeStyles } from "@mui/styles";
import DOMPurify from "dompurify";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import type { ExtensionEventResponses } from "../../../types/browser";
import ConfigLogo from "../../components/config-logo";
import DialogUnlockOfflineCache from "../../components/dialogs/unlock-offline-cache";
import GridContainerErrors from "../../components/grid-container-errors";
import browserClient from "../../services/browser-client";
import deviceService from "../../services/device";
import offlineCacheService from "../../services/offline-cache";
import user from "../../services/user";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
	},
	contentRoot: {
		display: "flex",
		padding: "15px",
	},
	appBar: {
		zIndex: theme.zIndex.drawer + 1,
		[theme.breakpoints.up("sm")]: {
			width: `calc(100% - 0px)`,
			marginLeft: 0,
		},
		backgroundColor: theme.palette.appBarBackground.main,
		color: theme.palette.appBarText.main,
		borderColor: theme.palette.appBarBackground.main,
		borderStyle: "solid",
		borderWidth: "1px",
		boxShadow: "none",
	},
	// necessary for content to be below app bar
	toolbar: {
		minHeight: deviceService.hasTitlebar() ? "82px" : "50px",
	},
	fullContent: {
		flexGrow: 1,
	},
	content: {
		height: "100%",
		width: "100%",
		overflow: "auto",
		position: "absolute",
		padding: "15px",
	},
	topLogo: {
		padding: "10px",
		height: "100%",
	},
	menuButton: {
		marginRight: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			display: "none",
		},
	},
	topMenuButton: {
		textTransform: "none",
	},
}));

const PopupPgpReadView = () => {
	const { gpgMessageId } = useParams<{ gpgMessageId: string }>();
	const classes = useStyles();
	const { t } = useTranslation();
	const [decryptedMessage, setDecryptedMessage] = useState<string | undefined>(
		"",
	);
	const [unlockOfflineCache, setUnlockOfflineCache] = useState(false);
	const [decrypting, setDecrypting] = useState(true);
	const [sender, setSender] = useState<string | null | undefined>(null);
	const [errors, setErrors] = useState<string[]>([]);

	let isSubscribed = true;
	React.useEffect(() => {
		if (offlineCacheService.isActive() && offlineCacheService.isLocked()) {
			setUnlockOfflineCache(true);
		} else {
			readGpg();
		}

		// cancel subscription to useEffect
		return () => {
			isSubscribed = false;
		};
	}, []);

	const readGpg = () => {
		browserClient.emitSec("read-gpg", gpgMessageId, (response) => {
			// Error replies have neither field; retain the existing undefined state values.
			const data = response as Exclude<
				ExtensionEventResponses["read-gpg"],
				{ error: string }
			>;
			if (!isSubscribed) {
				return;
			}
			if (Object.hasOwn(data, "plaintext")) {
				setDecrypting(false);
				setDecryptedMessage(data.plaintext!.data);
				setSender(data.sender);
			} else {
				setDecrypting(false);
				setDecryptedMessage(data.message);
				setSender(data.sender);
			}
		});
	};

	const onUnlockOfflineCacheClosed = () => {
		setUnlockOfflineCache(false);
		if (offlineCacheService.isActive() && offlineCacheService.isLocked()) {
			setUnlockOfflineCache(true);
		} else {
			readGpg();
		}
	};

	const logout = () => {
		user.logout();
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<AppBar position="fixed" className={classes.appBar}>
				<Container maxWidth="lg">
					<Toolbar>
						<IconButton
							color="inherit"
							aria-label="open drawer"
							edge="start"
							className={classes.menuButton}
							size="large"
						>
							<MenuIcon />
						</IconButton>
						<a className={classes.topLogo} href="#">
							<ConfigLogo
								configKey={"logo_inverse"}
								defaultLogo={"img/logo-inverse.png"}
								height="100%"
							/>
						</a>
						<div style={{ width: "100%" }}>
							<div style={{ float: "right" }}>
								<Button
									variant="contained"
									aria-controls="simple-menu"
									aria-haspopup="true"
									onClick={logout}
									color="primary"
									disableElevation
									className={classes.topMenuButton}
								>
									{t("LOGOUT")}
								</Button>
							</div>
						</div>
					</Toolbar>
				</Container>
			</AppBar>
			<div className={classes.fullContent}>
				<div className={classes.toolbar} />
				<div className={classes.content}>
					<Paper square>
						<AppBar elevation={0} position="static" color="default">
							<Toolbar>{t("DECRYPTED_MESSAGE")}</Toolbar>
						</AppBar>
						<div className={classes.contentRoot}>
							<Grid container>
								<GridContainerErrors errors={errors} setErrors={setErrors} />
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(decryptedMessage!, {
											USE_PROFILES: { html: true },
										}),
									}}
								/>
							</Grid>
						</div>
					</Paper>
				</div>
			</div>
			{unlockOfflineCache && (
				<DialogUnlockOfflineCache
					open={unlockOfflineCache}
					onClose={onUnlockOfflineCacheClosed}
				/>
			)}
		</div>
	);
};

export default PopupPgpReadView;
